"use client";

/**
 * Native HTML5 player for a manifest video. Mounted only after explicit
 * intent (inside the modal), so mounting it may start playback. Native
 * controls, Dutch captions available but off by default, playsInline, never
 * autoplay with sound from page load. On a load error it renders a
 * plain-language fallback instead of a dead player — the page never goes blank.
 */

import { useEffect, useRef, useState } from "react";
import { useT } from "@/i18n";
import type { MarketingVideo } from "@/lib/marketing-videos";
import { trackVideo } from "./video-analytics";

export function VideoPlayer({
  video,
  autoPlay = false,
}: {
  video: MarketingVideo;
  /** True only directly after a user gesture (poster click → modal). */
  autoPlay?: boolean;
}) {
  const t = useT();
  const ref = useRef<HTMLVideoElement | null>(null);
  const [errored, setErrored] = useState(false);
  // Fire each coarse event at most once per mount.
  const fired = useRef<Set<string>>(new Set());

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const once = (key: string, fn: () => void) => {
      if (fired.current.has(key)) return;
      fired.current.add(key);
      fn();
    };
    const onPlay = () => once("play", () => trackVideo(video.analyticsName, "play"));
    const onPause = () => {
      // Ignore the implicit pause that fires when playback ends.
      if (el.ended) return;
      once("pause", () => trackVideo(video.analyticsName, "pause"));
    };
    const onEnded = () => once("complete", () => trackVideo(video.analyticsName, "complete"));
    const onTime = () => {
      if (!el.duration) return;
      const pct = (el.currentTime / el.duration) * 100;
      if (pct >= 25) once("p25", () => trackVideo(video.analyticsName, "progress_25"));
      if (pct >= 50) once("p50", () => trackVideo(video.analyticsName, "progress_50"));
      if (pct >= 75) once("p75", () => trackVideo(video.analyticsName, "progress_75"));
    };
    const onTracks = () => {
      for (let i = 0; i < el.textTracks.length; i++) {
        if (el.textTracks[i].mode === "showing") {
          once("cc", () => trackVideo(video.analyticsName, "captions_on"));
        }
      }
    };
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onEnded);
    el.addEventListener("timeupdate", onTime);
    el.textTracks.addEventListener?.("change", onTracks);
    return () => {
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onEnded);
      el.removeEventListener("timeupdate", onTime);
      el.textTracks.removeEventListener?.("change", onTracks);
    };
  }, [video.analyticsName]);

  if (errored) {
    return (
      <div className="flex aspect-video w-full flex-col items-center justify-center gap-3 rounded-xl border border-line-subtle bg-bg-raised p-8 text-center">
        <div className="text-[15px] font-semibold text-fg-bright">{t("videoKit.errorTitle")}</div>
        <p className="max-w-md text-[13px] leading-relaxed text-fg-dim">{t("videoKit.errorBody")}</p>
      </div>
    );
  }

  return (
    <video
      ref={ref}
      className="block aspect-video w-full rounded-lg bg-bg-base"
      controls
      playsInline
      autoPlay={autoPlay}
      preload="metadata"
      poster={video.poster?.jpg}
      width={video.width}
      height={video.height}
      crossOrigin="anonymous"
      onError={() => setErrored(true)}
    >
      <source src={video.mp4} type="video/mp4" />
      {/* Captions ship with every full video but start OFF — viewers enable
          them in the native player when they want them. */}
      {video.captionsVtt && (
        <track
          kind="captions"
          src={video.captionsVtt}
          srcLang="nl"
          label={t("videoKit.captionsLabel")}
        />
      )}
    </video>
  );
}
